/* ── 209 §26 [Laurent] — THE BELL: TODAY'S, THEN WHAT IS LATE ─────────────────
 *
 * « The Bell popout is overcrowded with old notifications, reminders, etc.
 * Should show only (a) the notifications of the day, including the chat
 * messages …, and under, (b) the tasks which are late. » Addendum 6: tomorrow's
 * events stay, in (a), after today's rows.
 *
 * WHERE A ROW LANDS is stated by the row, where the aggregator builds it
 * (App.tsx, `notifications`) — never re-derived from its id or its type, the
 * rule 45 §2 earned ("a fact the row already states must not be re-derived at
 * a second site"). Every source, its day and its place is the census,
 * docs/209-deploy-note.md §10:
 *
 *   "today"    — (a): it fired today, it is about today, or it is a warning
 *                the app raises afresh each day (an ID document about to
 *                expire, the Outlook import, a CDD review coming due);
 *   "tomorrow" — (a) too, after today's rows: tomorrow's events;
 *   "late"     — (b): the overdue group as it stood — nothing late leaves;
 *   "out"      — everything else leaves the bell: a past day's reminder,
 *                alert, notice or mention (an unread mention stays unread in
 *                its chat). The running timer's row is gone from the
 *                aggregator (it has its own place, the column).
 *
 * A row that states NO place stays, in (a): omitting a row the aggregator
 * built is the defect 128 §3 refused ("the alarm goes from mute to absent").
 */
export type BellPlace = "today" | "tomorrow" | "late" | "out";

const dayOf = (v: unknown): string => {
  if (typeof v === "string") return v.slice(0, 10);
  return v instanceof Date ? v.toISOString().slice(0, 10) : "";
};

/** A firing — a stored notice, a chat mention: its own day decides. */
export function firedPlace(when: unknown, today: string): BellPlace {
  return dayOf(when) === today ? "today" : "out";
}

/** A reminder (a task's, an event's): late when its item is (the overdue group
 *  as it stood); about today when its item falls today; else the day it fired. */
export function reminderPlace(o: { late: boolean; dueToday: boolean; firedOn: unknown }, today: string): BellPlace {
  if (o.late) return "late";
  if (o.dueToday) return "today";
  return firedPlace(o.firedOn, today);
}

/** An event: today's, tomorrow's (addendum 6), else out. */
export function eventPlace(date: unknown, today: string, tomorrow: string): BellPlace {
  const d = dayOf(date);
  if (d === today) return "today";
  return d === tomorrow ? "tomorrow" : "out";
}

/** A warning the app raises afresh each day: late, or today's. */
export function standingPlace(late: boolean): BellPlace {
  return late ? "late" : "today";
}

/** THE BELL'S LIST: the rows that stay, in their order. */
export function bellRows<T extends { _bell?: BellPlace }>(rows: readonly T[]): T[] {
  return rows.filter((r) => r._bell !== "out");
}

/** THE TWO GROUPS, in the order they print: (a) today's rows, then tomorrow's
 *  events (addendum 6); (b) what is late. Stable within each — the aggregator's
 *  own order (newest first) is kept. Total: a row that states no place is (a)'s. */
export function bellGroups<T extends { _bell?: BellPlace }>(rows: readonly T[]): { today: T[]; late: T[] } {
  const rest = rows.filter((r) => r._bell !== "late");
  return {
    today: [...rest.filter((r) => r._bell !== "tomorrow"), ...rest.filter((r) => r._bell === "tomorrow")],
    late: rows.filter((r) => r._bell === "late"),
  };
}

/** Addendum 6 — THE BADGE: the rows the bell shows now that were not among
 *  the rows it showed when it was last opened (`seen`, kept per user where the
 *  read marks are kept). Not "unread": a row read elsewhere but never shown by
 *  a bell still counts once; a row shown and left unread counts no more. */
export function bellNewCount(rows: readonly { id?: unknown }[], seen: readonly unknown[]): number {
  const had = new Set(seen.map((x) => String(x)));
  return rows.filter((r) => !had.has(String(r.id))).length;
}

/** The ids a bell records when it shows its list. */
export function bellIds(rows: readonly { id?: unknown }[]): string[] {
  return rows.map((r) => String(r.id));
}

/* ── 213B §10 [RULED 43] — ONE ROW PER LATE TASK: the readers App.tsx's row builder needs, taking `unknown` (the rows
   and the persisted payloads are untyped there, and App.tsx's ESLint count may not rise — 213 addendum 3 (b)). */
/** A task row's id as a number, or null. */
export function taskIdOf(row: unknown): number | null {
  const id = (row as { id?: unknown } | null | undefined)?.id;
  if (typeof id === "number" && Number.isFinite(id)) return id;
  if (typeof id === "string" && id.trim() && Number.isFinite(Number(id))) return Number(id);
  return null;
}
/** Whether a persisted notice is the engine's reminder for a task whose standing « En retard » row stands. */
export function isLateTaskReminder(payload: unknown, late: ReadonlySet<number>): boolean {
  const p = payload as { kind?: unknown; entity?: unknown; entityId?: unknown } | null | undefined;
  return !!p && p.kind === "task_reminder" && p.entity === "task" && late.has(Number(p.entityId));
}
/** 213B §6 — an alert toast's join link, or null (the card's join line renders only with one). */
export function joinUrlOf(toast: unknown): string | null {
  const u = (toast as { joinUrl?: unknown } | null | undefined)?.joinUrl;
  return typeof u === "string" && u ? u : null;
}
